import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck2, Wallet } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { DashboardPaymentsToday } from '@/types/dashboard';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';

/** Os totais chegam NEGATIVOS (saídas); a tela mostra o módulo como "a pagar". */
const toPay = (v: number): string => formatCurrencyBRL(Math.abs(v));

/**
 * Painel "Pagamentos previstos hoje" (admin/finance): total por empresa e,
 * dentro de cada uma, por conta corrente.
 */
export const PaymentsTodayPanel: React.FC<{ data: DashboardPaymentsToday }> = ({ data }) => (
  <Card>
    <CardContent className="p-0">
      {data.companies.length === 0 ? (
        <div className="p-4 flex items-start gap-3">
          <CalendarCheck2 className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-sm font-medium text-emerald-800">Nenhum pagamento previsto para hoje</p>
        </div>
      ) : (
        <>
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Wallet className="text-slate-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-slate-700">
                {data.count} lançamento(s) a pagar em {formatDateBR(data.date)}
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Total a pagar: <span className="text-lg font-semibold text-red-700">{toPay(data.total)}</span>
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Empresa / Conta</th>
                  <th className="text-right font-medium px-4 py-2">Lançamentos</th>
                  <th className="text-right font-medium px-4 py-2">A pagar</th>
                </tr>
              </thead>
              {data.companies.map((c) => (
                <tbody key={c.company_id} className="divide-y divide-slate-100 border-b border-slate-200">
                  <tr className="bg-slate-50/60">
                    <td className="px-4 py-2 font-medium text-slate-800">{c.company_name}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{c.count}</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{toPay(c.total)}</td>
                  </tr>
                  {c.accounts.map((a) => (
                    <tr key={a.account_id}>
                      <td className="px-4 py-2 pl-8 text-slate-700">{a.account_name}</td>
                      <td className="px-4 py-2 text-right text-slate-600">{a.count}</td>
                      <td className="px-4 py-2 text-right text-slate-700 whitespace-nowrap">{toPay(a.total)}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </>
      )}

      <div className="px-4 py-3 border-t border-slate-200">
        <Link to="/financial/transactions" className="text-sm font-medium text-blue-700 hover:underline">
          Abrir Conta Corrente →
        </Link>
      </div>
    </CardContent>
  </Card>
);
